import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, ArrowLeft, RefreshCw } from 'lucide-react';
import api from '../../services/api';

const SystemLogs = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('system'); // 'system' or 'notifications'
    const [systemLogs, setSystemLogs] = useState([]);
    const [notificationLogs, setNotificationLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        level: '',
        source: '',
        type: '',
        status: ''
    });

    useEffect(() => {
        fetchLogs();
    }, [activeTab]);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            if (activeTab === 'system') {
                const params = new URLSearchParams();
                if (filters.startDate) params.append('startDate', filters.startDate);
                if (filters.endDate) params.append('endDate', filters.endDate);
                if (filters.level) params.append('level', filters.level);
                if (filters.source) params.append('source', filters.source);

                const res = await api.get(`/logs/system?${params.toString()}`);
                setSystemLogs(res.data.logs);
            } else {
                const params = new URLSearchParams();
                if (filters.startDate) params.append('startDate', filters.startDate);
                if (filters.endDate) params.append('endDate', filters.endDate);
                if (filters.type) params.append('type', filters.type);
                if (filters.status) params.append('status', filters.status);

                const res = await api.get(`/logs/notifications?${params.toString()}`);
                setNotificationLogs(res.data.logs);
            }
        } catch (error) {
            console.error('Failed to fetch logs:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        try {
            const params = new URLSearchParams();
            if (filters.startDate) params.append('startDate', filters.startDate);
            if (filters.endDate) params.append('endDate', filters.endDate);

            if (activeTab === 'system') {
                if (filters.level) params.append('level', filters.level);
                if (filters.source) params.append('source', filters.source);
                window.open(`${api.defaults.baseURL}/logs/system/download?${params.toString()}`, '_blank');
            } else {
                if (filters.type) params.append('type', filters.type);
                if (filters.status) params.append('status', filters.status);
                window.open(`${api.defaults.baseURL}/logs/notifications/download?${params.toString()}`, '_blank');
            }
        } catch (error) {
            console.error('Failed to download logs:', error);
        }
    };

    const getLevelColor = (level) => {
        const colors = {
            INFO: '#3b82f6',
            WARN: '#f59e0b',
            ERROR: '#ef4444',
            CRITICAL: '#dc2626'
        };
        return colors[level] || '#6b7280';
    };

    return (
        <div className="container section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <button onClick={() => navigate('/admin/config')} className="btn btn-secondary" style={{ padding: '0.5rem' }}>
                        <ArrowLeft size={20} />
                    </button>
                    <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold' }}>System Logs</h1>
                </div>
                <div style={{ display: 'flex', gap: '1rem' }}>
                    <button onClick={fetchLogs} className="btn btn-secondary">
                        <RefreshCw size={18} style={{ marginRight: '0.5rem' }} /> Refresh
                    </button>
                    <button onClick={handleDownload} className="btn btn-primary">
                        <Download size={18} style={{ marginRight: '0.5rem' }} /> Download CSV
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', borderBottom: '2px solid #e5e7eb' }}>
                <button
                    onClick={() => setActiveTab('system')}
                    style={{
                        padding: '0.75rem 1.5rem',
                        background: 'none',
                        border: 'none',
                        borderBottom: activeTab === 'system' ? '2px solid var(--primary)' : 'none',
                        color: activeTab === 'system' ? 'var(--primary)' : '#6b7280',
                        fontWeight: activeTab === 'system' ? '600' : '400',
                        cursor: 'pointer',
                        marginBottom: '-2px'
                    }}
                >
                    System Action Logs
                </button>
                <button
                    onClick={() => setActiveTab('notifications')}
                    style={{
                        padding: '0.75rem 1.5rem',
                        background: 'none',
                        border: 'none',
                        borderBottom: activeTab === 'notifications' ? '2px solid var(--primary)' : 'none',
                        color: activeTab === 'notifications' ? 'var(--primary)' : '#6b7280',
                        fontWeight: activeTab === 'notifications' ? '600' : '400',
                        cursor: 'pointer',
                        marginBottom: '-2px'
                    }}
                >
                    Notification Logs
                </button>
            </div>

            {/* Filters */}
            <div className="glass-card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>Start Date</label>
                        <input
                            type="date"
                            value={filters.startDate}
                            onChange={e => setFilters({ ...filters, startDate: e.target.value })}
                            style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>End Date</label>
                        <input
                            type="date"
                            value={filters.endDate}
                            onChange={e => setFilters({ ...filters, endDate: e.target.value })}
                            style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                        />
                    </div>
                    {activeTab === 'system' ? (
                        <>
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>Level</label>
                                <select
                                    value={filters.level}
                                    onChange={e => setFilters({ ...filters, level: e.target.value })}
                                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                                >
                                    <option value="">All</option>
                                    <option value="INFO">INFO</option>
                                    <option value="WARN">WARN</option>
                                    <option value="ERROR">ERROR</option>
                                    <option value="CRITICAL">CRITICAL</option>
                                </select>
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>Source</label>
                                <select
                                    value={filters.source}
                                    onChange={e => setFilters({ ...filters, source: e.target.value })}
                                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                                >
                                    <option value="">All</option>
                                    <option value="AUTH">AUTH</option>
                                    <option value="API">API</option>
                                    <option value="ADMIN">ADMIN</option>
                                    <option value="JOB">JOB</option>
                                </select>
                            </div>
                        </>
                    ) : (
                        <>
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>Type</label>
                                <input
                                    type="text"
                                    value={filters.type}
                                    onChange={e => setFilters({ ...filters, type: e.target.value })}
                                    placeholder="e.g., LEAVE_APPLIED"
                                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>Status</label>
                                <select
                                    value={filters.status}
                                    onChange={e => setFilters({ ...filters, status: e.target.value })}
                                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                                >
                                    <option value="">All</option>
                                    <option value="SENT">SENT</option>
                                    <option value="FAILED">FAILED</option>
                                    <option value="PENDING">PENDING</option>
                                </select>
                            </div>
                        </>
                    )}
                    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <button onClick={fetchLogs} className="btn btn-primary" style={{ width: '100%' }}>
                            Apply Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Logs Table */}
            <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
                {loading ? (
                    <div style={{ padding: '3rem', textAlign: 'center' }}>Loading...</div>
                ) : activeTab === 'system' ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                            <tr>
                                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Timestamp</th>
                                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Level</th>
                                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Source</th>
                                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Message</th>
                                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>User</th>
                            </tr>
                        </thead>
                        <tbody>
                            {systemLogs.length === 0 ? (
                                <tr>
                                    <td colSpan="5" style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                                        No logs found
                                    </td>
                                </tr>
                            ) : (
                                systemLogs.map(log => (
                                    <tr key={log.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                                            {new Date(log.createdAt).toLocaleString()}
                                        </td>
                                        <td style={{ padding: '1rem' }}>
                                            <span style={{
                                                padding: '0.25rem 0.75rem',
                                                borderRadius: '9999px',
                                                fontSize: '0.75rem',
                                                fontWeight: '600',
                                                color: 'white',
                                                backgroundColor: getLevelColor(log.level)
                                            }}>
                                                {log.level}
                                            </span>
                                        </td>
                                        <td style={{ padding: '1rem', fontWeight: '500' }}>{log.source}</td>
                                        <td style={{ padding: '1rem', fontSize: '0.875rem' }}>{log.message}</td>
                                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                                            {log.user?.name || 'System'}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                            <tr>
                                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Timestamp</th>
                                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Type</th>
                                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Recipient</th>
                                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Subject</th>
                                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {notificationLogs.length === 0 ? (
                                <tr>
                                    <td colSpan="5" style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                                        No logs found
                                    </td>
                                </tr>
                            ) : (
                                notificationLogs.map(log => (
                                    <tr key={log.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                                            {new Date(log.createdAt).toLocaleString()}
                                        </td>
                                        <td style={{ padding: '1rem', fontWeight: '500' }}>{log.type}</td>
                                        <td style={{ padding: '1rem', fontSize: '0.875rem' }}>{log.recipientEmail}</td>
                                        <td style={{ padding: '1rem', fontSize: '0.875rem' }}>{log.subject}</td>
                                        <td style={{ padding: '1rem' }}>
                                            <span style={{
                                                padding: '0.25rem 0.75rem',
                                                borderRadius: '9999px',
                                                fontSize: '0.75rem',
                                                fontWeight: '600',
                                                color: 'white',
                                                backgroundColor: log.status === 'SENT' ? '#22c55e' : log.status === 'FAILED' ? '#ef4444' : '#f59e0b'
                                            }}>
                                                {log.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default SystemLogs;
